/**
 * Payment Terms Form Component
 * Dialog form for creating/editing payment terms with template support
 */

import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Box,
  Typography,
  Chip,
  Grid,
  Divider,
  CircularProgress,
} from "@mui/material";
import { AccountBalance } from "@mui/icons-material";
import {
  createPaymentTerms,
  updatePaymentTerms,
  getPaymentTermsTemplates,
  createPaymentTermsFromTemplate,
} from "../services/paymentTermsService";

const PAYOUT_TRIGGERS = {
  FULL_PAYMENT: "Full Payment Required",
  DEPOSIT_ONLY: "Deposit Only",
  COMPLETION_PLUS_DAYS: "Completion + Days Delay",
  PAYDAY_ONLY: "Monthly Payday Only (No Advance)",
  PAYDAY_WITH_ADVANCE: "Monthly Payday (Advance Allowed)",
};

const PaymentTermsForm = ({ open, onClose, providerId, providerName, existingTerms, onSave }) => {
  const [formData, setFormData] = useState({
    payoutTrigger: "FULL_PAYMENT",
    retentionPercent: 0,
    disputeWindowDays: 7,
    payoutDelayDays: null,
  });
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  useEffect(() => {
    if (open) {
      loadTemplates();
      if (existingTerms) {
        setFormData({
          payoutTrigger: existingTerms.payoutTrigger || "FULL_PAYMENT",
          retentionPercent: existingTerms.retentionPercent || 0,
          disputeWindowDays: existingTerms.disputeWindowDays || 7,
          payoutDelayDays: existingTerms.payoutDelayDays || null,
        });
      } else {
        setFormData({
          payoutTrigger: "FULL_PAYMENT",
          retentionPercent: 0,
          disputeWindowDays: 7,
          payoutDelayDays: null,
        });
      }
      setError(null);
    }
  }, [open, existingTerms]);

  const loadTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const result = await getPaymentTermsTemplates();
      if (result.success) {
        setTemplates(result.data || []);
      }
    } catch (err) {
      console.error("Failed to load templates:", err);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handleTemplateSelect = async (templateId) => {
    setLoading(true);
    setError(null);
    try {
      const result = await createPaymentTermsFromTemplate(providerId, templateId);
      if (result.success) {
        onSave();
        onClose();
      } else {
        setError(result.error || "Failed to create from template");
      }
    } catch (err) {
      setError(err.message || "Failed to create from template");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);

    // Validate
    if (formData.payoutTrigger === "COMPLETION_PLUS_DAYS" && !formData.payoutDelayDays) {
      setError("Payout delay days is required for this trigger type");
      setLoading(false);
      return;
    }

    try {
      let result;
      if (existingTerms) {
        result = await updatePaymentTerms(providerId, formData);
      } else {
        result = await createPaymentTerms({
          providerId,
          ...formData,
        });
      }

      if (result.success) {
        onSave();
        onClose();
      } else {
        setError(result.error || "Failed to save payment terms");
      }
    } catch (err) {
      setError(err.message || "Failed to save payment terms");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <AccountBalance />
          {existingTerms ? "Edit" : "Create"} Payment Terms for {providerName}
        </Box>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {!existingTerms && (
          <>
            {/* Template Selection */}
            <Box mb={3}>
              <Typography variant="subtitle2" gutterBottom>
                Quick Setup from Template
              </Typography>
              {loadingTemplates ? (
                <Box display="flex" justifyContent="center" p={2}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <Box display="flex" gap={1} flexWrap="wrap">
                  {templates.map((template) => (
                    <Chip
                      key={template.id}
                      label={template.name}
                      onClick={() => handleTemplateSelect(template.id)}
                      disabled={loading}
                      color="primary"
                      variant="outlined"
                      sx={{ cursor: "pointer" }}
                    />
                  ))}
                </Box>
              )}
            </Box>

            <Divider sx={{ my: 2 }} />
          </>
        )}

        {/* Custom Form */}
        <Typography variant="subtitle2" gutterBottom>
          {existingTerms ? "Update" : "Configure"} Payment Terms
        </Typography>

        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Payout Trigger</InputLabel>
              <Select
                value={formData.payoutTrigger}
                onChange={(e) =>
                  setFormData({ ...formData, payoutTrigger: e.target.value })
                }
                label="Payout Trigger"
                disabled={loading}
              >
                {Object.entries(PAYOUT_TRIGGERS).map(([key, label]) => (
                  <MenuItem key={key} value={key}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {formData.payoutTrigger === "COMPLETION_PLUS_DAYS" && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="number"
                label="Payout Delay (Days)"
                value={formData.payoutDelayDays || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    payoutDelayDays: parseInt(e.target.value) || null,
                  })
                }
                inputProps={{ min: 0 }}
                disabled={loading}
                required
              />
            </Grid>
          )}

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label="Retention Percentage"
              value={formData.retentionPercent}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  retentionPercent: parseFloat(e.target.value) || 0,
                })
              }
              inputProps={{ min: 0, max: 100, step: 0.1 }}
              helperText="Platform fee percentage applied to provider payouts (0-100)"
              disabled={loading}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label="Dispute Window (Days)"
              value={formData.disputeWindowDays}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  disputeWindowDays: parseInt(e.target.value) || 7,
                })
              }
              inputProps={{ min: 0 }}
              disabled={loading}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || (formData.payoutTrigger === "COMPLETION_PLUS_DAYS" && !formData.payoutDelayDays)}
        >
          {loading ? "Saving..." : existingTerms ? "Update" : "Create"} Payment Terms
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PaymentTermsForm;
